import { Store } from "lucide-react";

import {
  Card,
  CardHeader,
  EmptyState,
  Pill,
  SkeletonRows,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
} from "@/components/ui";

import { useBranches, useSections } from "../useSettingsData";

/**
 * Branches and the sections inside them.
 *
 * Read-only for now: creating a branch or section is a rare, consequential act
 * (a branch cannot be deleted once it has traded, only archived), and the
 * endpoints exist. A guided form is Phase 2 work rather than something to bolt
 * on beside a list.
 */
export function BranchesTab() {
  const branches = useBranches();
  const sections = useSections();

  if (branches.isLoading || sections.isLoading)
    return <SkeletonRows rows={4} />;

  if (!branches.data?.length) {
    return (
      <Card flush>
        <EmptyState
          icon={Store}
          title="No branches"
          description="Every business has at least one."
        />
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      {branches.data.map((branch) => {
        const own = (sections.data ?? []).filter((s) => s.branch === branch.id);

        return (
          <Card key={branch.id} flush className="p-5">
            <CardHeader
              title={
                <span className="flex items-center gap-2">
                  {branch.name}
                  {branch.is_primary && <Pill tone="info">Main</Pill>}
                  {!branch.is_active && <Pill tone="neutral">Archived</Pill>}
                </span>
              }
              subtitle={
                [branch.suburb, branch.city].filter(Boolean).join(", ") ||
                `Code ${branch.code}`
              }
              action={
                <Pill
                  tone={
                    branch.stock_grain === "SECTION" ? "success" : "neutral"
                  }
                >
                  {branch.stock_grain === "SECTION"
                    ? "Stock tracked per section"
                    : "Stock pooled at branch"}
                </Pill>
              }
            />

            <Table className="mt-3">
              <THead>
                <TH>Section</TH>
                <TH hideBelow="sm">Code</TH>
                <TH hideBelow="md">Type</TH>
                <TH>Stock</TH>
              </THead>
              <TBody>
                {own.map((section) => (
                  <TR key={section.id}>
                    <TD className="font-medium">
                      <span className="flex items-center gap-2">
                        {section.name}
                        {/* Every branch has exactly one, created with it. It is
                            where stock posts when a branch does not track by
                            section, so it cannot be removed. */}
                        {section.is_default && (
                          <Pill tone="neutral">Default</Pill>
                        )}
                      </span>
                    </TD>
                    <TD hideBelow="sm" className="font-numeric">
                      {section.code}
                    </TD>
                    <TD hideBelow="md" className="text-ink-600">
                      {section.section_type.replace(/_/g, " ").toLowerCase()}
                    </TD>
                    <TD className="text-ink-600">
                      {section.holds_stock ? "Holds stock" : "No stock"}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </Card>
        );
      })}
    </div>
  );
}
